import { PRODUCTS } from '../config/columns.config';

const SHORT_KEY_TO_PRODUCT = {
    'NK': 'nolteKuechen',
    'EK': 'expressKuechen',
    'NE': 'nolteEco',
    'NS': 'nolteSPA',
    'NN': 'nolteNeo'
};

export const transformData = (customers, salesData, budgetData, vendorsData, showroomsData, budgetType, projectDetails) => {

    const showroomLookup = new Map();
    if (Array.isArray(showroomsData)) {
        showroomsData.forEach(sh => {
            showroomLookup.set(String(sh.id), sh.name);
        });
    }

    const vendorProductMap = {};
    const vendorNameMap = {};
    if (Array.isArray(vendorsData)) {
        vendorsData.forEach(vendor => {
            const productKey = SHORT_KEY_TO_PRODUCT[vendor.shortKey];
            if (productKey) {
                vendorProductMap[String(vendor.id)] = productKey;
                if (vendor.name) {
                    vendorNameMap[vendor.name.toUpperCase().trim()] = productKey;
                }
            }
        });
    }

    const customerMap = new Map();
    if (Array.isArray(customers)) {
        customers.forEach(company => {
            customerMap.set(String(company.id), {
                id: company.id.toString(),
                name: company.name,
                territory: company.territory ?? 'Unknown',
                retail: {},
                projects: [],
                showroom: {},
                internalBudget: 0,
                externalBudget: 0,
                retailBudget: 0,
                projectBudget: 0,
                hasData: false,
                subAccounts: [],
            });
        });
    }

    if (Array.isArray(showroomsData)) {
        showroomsData.forEach(sh => {
            const parentId = String(sh.parentCompanyNumber);
            const showroomId = String(sh.id);

            const parent = customerMap.get(parentId);

            if (parent && showroomId !== parentId) {
                const exists = parent.subAccounts.find(s => s.id === showroomId);

                if (!exists) {
                    parent.subAccounts.push({
                        id: showroomId,
                        name: sh.name,
                        territory: parent.territory,
                        retail: {},
                        showroom: {},
                        projects: [],
                        subAccounts: [],
                        internalBudget: 0,
                        externalBudget: 0,
                        retailBudget: 0,
                        projectBudget: 0
                    });
                }
            }
        });
    }

    const getTargetObject = (parent, showroomNum, custId) => {
        const sNum = String(showroomNum);
        const cNum = String(custId);

        const isMain = sNum === '0' || sNum === cNum;

        if (isMain) {
            return parent;
        }

        let sub = parent.subAccounts.find(s => s.id === sNum);

        if (!sub) {
            const realShowroomName = showroomLookup.get(sNum) || `Showroom ${showroomNum}`;
            sub = {
                id: sNum,
                name: realShowroomName,
                territory: parent.territory,
                retail: {},
                showroom: {},
                projects: [],
                subAccounts: [],
                internalBudget: 0,
                externalBudget: 0,
                retailBudget: 0,
                projectBudget: 0
            };
            parent.subAccounts.push(sub);
        }
        return sub;
    };

    if (Array.isArray(salesData)) {
        salesData.forEach(sale => {
            const custId = sale.customerNum;
            const showroomNum = sale.showroomNum;
            const vendorNum = sale.vendorNum;

            let parent = customerMap.get(String(custId));
            
            if (!parent) {
                parent = {
                    id: String(custId),
                    name: `Unknown Customer (${custId})`,
                    territory: 'Unknown',
                    retail: {},
                    projects: [],
                    showroom: {},
                    internalBudget: 0,
                    externalBudget: 0,
                    retailBudget: 0,
                    projectBudget: 0,
                    hasData: false,
                    subAccounts: [],
                };
                customerMap.set(String(custId), parent);
            }

            let productKey = vendorProductMap[String(vendorNum)];
            if (!productKey && sale.vendorName) {
                productKey = vendorNameMap[sale.vendorName.toUpperCase().trim()];
            }
            if (!productKey) return;

            parent.hasData = true;

            const targetObj = getTargetObject(parent, showroomNum, custId);

            if (sale.category === 'RET') {
                targetObj.retail[productKey] = (targetObj.retail[productKey] || 0) + sale.total;
            }
            else if (sale.category === 'SHR') {
                targetObj.showroom[productKey] = (targetObj.showroom[productKey] || 0) + sale.total;
            }
            else if (['PRJ', 'CPRJ', 'MOU'].includes(sale.category)) {
                let projectBucket = targetObj.projects.find(p => p.id === 'GEN-PRJ');

                if (!projectBucket) {
                    projectBucket = {
                        id: 'GEN-PRJ',
                        name: 'Consolidated Projects',
                        isConsolidated: true,
                        total: 0,
                        ...Object.fromEntries(PRODUCTS.map(p => [p.key, 0]))
                    };
                    targetObj.projects.push(projectBucket);
                }
                projectBucket[productKey] += sale.total;
                projectBucket.total += sale.total;
            }
        });
    }

    if (Array.isArray(projectDetails)) {
        projectDetails.forEach(proj => {
            const custId = proj.customerNum;
            const showroomNum = proj.showroomNum;
            const vendorNum = proj.vendorNum;

            const parent = customerMap.get(String(custId));
            if (!parent) return;

            let productKey = vendorProductMap[String(vendorNum)];
            if (!productKey && proj.vendorName) {
                productKey = vendorNameMap[proj.vendorName.toUpperCase().trim()];
            }
            if (!productKey) return;
            parent.hasData = true;
            const targetObj = getTargetObject(parent, showroomNum, custId);

            let existingProject = targetObj.projects.find(p => String(p.id) === String(proj.projectCode));

            if (!existingProject) {
                existingProject = {
                    id: String(proj.projectCode),
                    name: proj.projectName || proj.projectCode,
                    total: 0,
                    ...Object.fromEntries(PRODUCTS.map(p => [p.key, 0]))
                };
                targetObj.projects.push(existingProject);
            }
            const value = proj.total || 0;
            existingProject[productKey] += value;
            existingProject.total += value;
            
            const consolidatedBucket = targetObj.projects.find(p => p.id === 'GEN-PRJ');
            if (consolidatedBucket) {
                consolidatedBucket[productKey] -= value;
                consolidatedBucket.total -= value;
                
                if (Math.abs(consolidatedBucket.total) < 0.01) {
                     consolidatedBucket.isHidden = true;
                }
            }
        });
    }

    if (Array.isArray(budgetData)) {
        budgetData.forEach(b => {
            const custId = b.customerNum;
            const showroomNum = b.showroomNum;

            const parent = customerMap.get(String(custId));
            if (!parent) return;

            parent.hasData = true;

            const targetObj = getTargetObject(parent, showroomNum, custId);

            if (budgetType === 'external') {
                targetObj.externalBudget = (targetObj.externalBudget || 0) + b.budget;
            } else {
                targetObj.internalBudget = (targetObj.internalBudget || 0) + b.budget;
            }

            if (b.categoryGroup === 'RET' || b.categoryGroup === 'SHR') {
                targetObj.retailBudget = (targetObj.retailBudget || 0) + b.budget;
            } else if (['PRJ', 'CPRJ', 'MOU'].includes(b.categoryGroup)) {
                targetObj.projectBudget = (targetObj.projectBudget || 0) + b.budget;
            }
        });
    }

    const sortById = (a, b) => a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' });

    return Array.from(customerMap.values())
        .filter(c => c.hasData || c.subAccounts.length > 0)
        .map(customer => {
            if (customer.subAccounts && customer.subAccounts.length > 0) {
                customer.subAccounts.sort(sortById);
            }
            return customer;
        })
        .sort(sortById);
};